import React, { useEffect, useState } from "react";
import {
  useGetAssessmentReports,
  useGetVQAActivity,
  useGetJoinVQADetails,
  useJoinVqaRequest,
  useDocumentAllFiles,
  useDocumentFile,
} from "../../custom-hooks/queries";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  FolderPlusIcon,
  MapPinIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { Icon } from "@iconify/react/dist/iconify.js";
import Skeleton from "react-loading-skeleton";
import { calculateDaysAgo, dateFormat, useUserRole } from "../../utils/helperMethods";
import AdminDetailsLoader from "../../components/Skeletons/adminDetailsLoader";
import Capabilities from "../../components/Capabilities";
import { AssessmentDetailBannerOrgNames } from "../../components/common/DetailBannerOrgNames";
import ReportAndActivitySkeletonLoader from "../../components/Skeletons/ReportAndActivitySkeletonLoader";
import StatusProgressBar from "../../components/ProgressBar";
import CustomButton from "../../components/CustomButton";
import NotificationModal from "../../components/NotificationModal";
import JoinAssessmentConfirmationModal from "../../components/JoinAssesmentConfirmationModal";
import StatusForDrawer from "../../components/StatusForDrawer";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { getCountryByValue, getStateOptions } from "../../utils/locationData";
import { getVQALocationFromProvider } from "../../utils/addressUtils";
import VqaOverviewContactsSection from "../../components/VqaOverviewContactsSection";
import SponsorRestrictedDetailsPanel from "../../components/Assessments/SponsorRestrictedDetailsPanel";
import { resolveRestrictedDetailsMode } from "../../utils/assessmentCalendarNavigation";

export const JoinvqaDetailAssessment = () => {
  const { id } = useParams();
  const userRole = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { datePassed } = location.state || {};

  const {
    data: assessmentDetails,
    isLoading,
    refetch: assessmentDetailsRefetch,
  } = useGetJoinVQADetails(id);

  const { data: activity, refetch: activityRefetch } = useGetVQAActivity(
    assessmentDetails?.vqa?.vqa_id
  );
  const [openModal, setOpenModal] = useState(false);
  // Notification modal states
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationDesc, setNotificationDesc] = useState("");
  const [notificationError, setNotificationError] = useState(false);
  const [requestSuccessful, setRequestSuccessful] = useState(false);

  const handleModalClose = () => {
    navigate("/RequestAssesmentList");
  };

  useEffect(() => {
    if (!notificationOpen && requestSuccessful) {
      handleModalClose();
      setRequestSuccessful(false);
    }
  }, [notificationOpen, requestSuccessful]);

  useEffect(() => {
    activityRefetch();
  }, [assessmentDetails]);

  const { data: allDocuments } = useGetAssessmentReports(
    assessmentDetails?.vqa?.vqa_id
  );
  const {
    mutate,
    data: files,
    isError: isFileError,
    error: errorMessageFile,
  } = useDocumentFile();
  const {
    mutate: allFilesDownload,
    data: allFiles,
    isError: allFilesError,
  } = useDocumentAllFiles();

  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");

  const handleDownload = (id) => {
    mutate(id, {
      onSuccess: () => {
        setShow(true);
        setMessage("Downloaded Successfully");
      },
      onError: () => {
        setShow(true);
        setMessage("Download Failed");
      },
    });
  };

  const handleDownloadAll = async (ids) => {
    allFilesDownload(ids); // Pass ids directly instead of using docsIds (state update is async)
  };

  const { mutate: joinVqa } = useJoinVqaRequest();

  // New handler that accepts the additional info from the modal
  const handleConfirmJoinVQA = (
    additionalInfo,
    selectedServices,
    desiredInitialDraftDate
  ) => {
    joinVqa(
      {
        id,
        additional_info: additionalInfo,
        selected_services: selectedServices,
        desired_initial_draft_date: desiredInitialDraftDate,
      },
      {
        onSuccess: () => {
          setNotificationTitle("Request Submitted");
          setNotificationDesc(
            "Your join request has been submitted successfully."
          );
          setNotificationError(false);
          setNotificationOpen(true);
          setRequestSuccessful(true);
        },
        onError: () => {
          setNotificationTitle("Request Failed");
          setNotificationDesc(
            "There was an error submitting your join request. Please try again."
          );
          setNotificationError(true);
          setNotificationOpen(true);
        },
      }
    );
  };

  let buttonText = "Join Scheduled Assessment";
  let buttonColor = datePassed ? "bg-gray-400" : "bg-indigo-600";
  let onClick = datePassed ? null : () => setOpenModal(true);
  let disabled = datePassed;
  let buttonWidth = "w-[120px]";
  let showViewDetailsButton = false;

  const restrictedMode = resolveRestrictedDetailsMode(assessmentDetails, {
    search: location.search,
    state: location.state,
  });
  if (restrictedMode) {
    const providerName =
      assessmentDetails?.provider?.provider_name ||
      assessmentDetails?.vqa?.provider?.provider_name;
    return (
      <>
        <SponsorRestrictedDetailsPanel
          mode={restrictedMode}
          providerName={providerName}
          onBack={() => navigate("/Assessment/Calendar")}
          onRequestToJoin={
            restrictedMode === "browse" && !datePassed ? () => setOpenModal(true) : undefined
          }
        />
        <JoinFlowModals
          openModal={openModal}
          setOpenModal={setOpenModal}
          onConfirm={handleConfirmJoinVQA}
          servicesScopes={assessmentDetails?.capabilities || []}
          notificationOpen={notificationOpen}
          setNotificationOpen={setNotificationOpen}
          notificationTitle={notificationTitle}
          notificationDesc={notificationDesc}
          notificationError={notificationError}
        />
      </>
    );
  }

  if (assessmentDetails?.is_already_requested) {
    buttonText = "Already Requested";
    buttonColor = "bg-red-400";
    disabled = true;
    buttonWidth = "w-auto";
  } else if (assessmentDetails?.is_assessment_closed) {
    buttonText = "Assessment Closed";
    buttonColor = "bg-red-400";
    disabled = true;
    buttonWidth = "w-auto";
  } else if (assessmentDetails?.is_sponsor_included) {
    showViewDetailsButton = true;
  } else if (assessmentDetails?.assessment_sponsor_count === 2) {
    buttonText = "Maximum Sponsors Reached";
    buttonColor = "bg-red-400";
    disabled = true;
    buttonWidth = "w-auto";
  }

  return (
    <div className="container">
      {isLoading ? (
        <Skeleton
          className="w-full h-32 p-7 border-y border-gray-300 bg-slate-50 mb-10"
          enableAnimation={true}
        />
      ) : (
        <div className="p-7 border-y border-gray-300 bg-slate-50 mb-10">
          <div className="flex items-center text-sm">
            <h1
              className="text-gray-500 cursor-pointer font-semibold"
              onClick={() => navigate("/RequestAssesmentList")}
            >
              Shared Assessments
            </h1>
            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
            <p className="ml-5 font-semibold">
              <span className="font-semibold">
                {assessmentDetails?.vqa_request_reference_number?.slice(0, 13)}{" "}
              </span>
            </p>
          </div>

          {datePassed && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded text-red-700">
              Date to join this VQA has passed.
            </div>
          )}

          <div className="mt-5 space-y-2 flex flex-col justify-start items-start md:justify-between md:items-center md:flex-row">
            <div className="flex items-center">
              <span className="bg-white border border-gray-300 rounded-md px-7 py-5 mr-5">
                <CheckBadgeIcon className="h-9 w-9 text-red-500" />
              </span>
              <span>
                <AssessmentDetailBannerOrgNames
                  details={assessmentDetails}
                  showSponsor={false}
                />
                <p className="text-sm text-gray-500 ">
                  Request Date:{" "}
                  <span className="font-semibold">
                    {assessmentDetails?.vqa === null
                      ? dateFormat(assessmentDetails?.vqa_created_on)
                      : dateFormat(assessmentDetails?.vqa_requested_on)}
                  </span>
                </p>
              </span>
            </div>

            <div className="flex gap-2">
              {showViewDetailsButton ? (
                <CustomButton
                  text="View Details"
                  width="100%"
                  height="h-10"
                  paddingRight="10px"
                  weight="font-semibold"
                  size="text-md"
                  color="bg-indigo-600"
                  textColor="text-white"
                  borderRadius="rounded-md"
                  border="none"
                  onClick={() =>
                    navigate(
                      `/Assessment/${assessmentDetails?.vqa_request_id}/Details`
                    )
                  }
                  padding="py-3 px-6"
                />
              ) : isLoading ? (
                <Skeleton width={15} height={15} />
              ) : (
                <CustomButton
                  text={buttonText}
                  width="100%"
                  height="h-10"
                  paddingRight="10px"
                  weight="font-semibold"
                  size="text-md"
                  color={buttonColor}
                  textColor="text-white"
                  borderRadius="rounded-md"
                  border="none"
                  onClick={onClick}
                  disabled={disabled}
                  padding="py-3 px-6"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-28 px-5 flex justify-center md:justify-between items-stretch flex-col md:flex-row">
        {isLoading ? (
          <div className="w-full md:w-[90%] border-r border-gray-300 px-3 mr-8 ">
            <AdminDetailsLoader />
          </div>
        ) : (
          <div className="w-full md:w-[90%] border-r border-gray-300 px-3 mr-8 ">
            <div className="border-b border-gray-300">
              <h1 className="text-sm font-medium text-gray-900">
                Assessment Details
              </h1>
            </div>

            <div className="my-5">
              <StatusProgressBar
                status={
                  assessmentDetails?.vqa_status ||
                  assessmentDetails?.vqa_request_status
                }
                vqa={assessmentDetails?.vqa ?? assessmentDetails}
              />
            </div>
            <div className="divide-y divide-gray-300">
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Assessment Type
                </h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.vqa !== null &&
                      assessmentDetails?.vqa_type
                    ? assessmentDetails.vqa_type.charAt(0).toUpperCase() +
                      assessmentDetails.vqa_type.slice(1)
                    : "-"}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">
                  Services in Scope
                </h2>
                <span className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  <Capabilities
                    services={assessmentDetails?.capabilities || []}
                    icon={true}
                    showLimited={false}
                  />
                </span>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">Location</h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex items-center">
                  <span className="mr-2">
                    <MapPinIcon className="w-4 h-4 fill-indigo-600 text-indigo-600" />
                  </span>
                  {assessmentDetails?.vqa_location
                    ?.is_secondary_location_required
                    ? assessmentDetails?.vqa_location
                        ?.secondary_location_address
                      : getVQALocationFromProvider(assessmentDetails?.provider, { short: false, abbvState: true }) || ""}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">Auditor</h2>
                <p className="mt-1 text-sm/6 text-gray-800 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.auditor?.vqa_auditor_name || "-"}
                </p>
              </div>
              <VqaOverviewContactsSection
                userRole={userRole}
                assessmentDetails={assessmentDetails}
                refetch={assessmentDetailsRefetch}
                activityRefetch={activityRefetch}
              />
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Start Date
                </h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex items-center">
                  <span className="mr-2">
                    <CalendarDaysIcon className="w-4 h-4 text-blue-800" />
                  </span>
                  {assessmentDetails?.assessment_start_date === null
                    ? "-"
                    : dateFormat(assessmentDetails?.assessment_start_date)}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Agreed Draft Date
                </h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex items-center">
                  <span className="mr-2">
                    <CalendarDaysIcon className="w-4 h-4 text-blue-800" />
                  </span>
                  {assessmentDetails?.vqa?.agreed_initial_draft_date === null
                    ? "-"
                    : dateFormat(assessmentDetails?.agreed_initial_draft_date)}
                </p>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <ReportAndActivitySkeletonLoader />
        ) : (
          <div className="w-full md:w-[30%] mt-10 md:mt-0">
            {
              <div className="border border-gray-300 rounded-md py-5 bg-slate-50">
                <div className="border-b border-gray-300 flex flex-col sm:flex-row flex-wrap justify-between items-stretch sm:items-center gap-3 px-3 sm:px-5 pb-2">
                  <h1 className="font-semibold text-md">Reports</h1>
                  {allDocuments?.items?.length > 0 && (
                    <div className="pb-2 flex-shrink-0">
                      <CustomButton
                        icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                        width={
                          allDocuments?.items?.length > 1 ? "w-40" : "w-32"
                        }
                        height={"h-10"}
                        color={"bg-white border border-gray-300"}
                        textColor={"text-gray-700"}
                        text={
                          allDocuments?.items?.length > 1
                            ? `Download All (${allDocuments?.items?.length ?? 0})`
                            : "Download"
                        }
                        onClick={() =>
                          handleDownloadAll(
                            allDocuments?.items?.map((doc) => doc.document_id)
                          )
                        }
                      />
                    </div>
                  )}
                </div>
                {allDocuments?.items?.length === 0 ? (
                  <div className="flex flex-col items-center mt-5">
                    <span className="flex justify-center items-center">
                      <FolderPlusIcon className="w-10 h-10 text-gray-300" />
                    </span>
                    <p className="mt-3 text-gray-700 text-sm font-semibold">
                      No Reports
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="py-5 p-5">
                      {allDocuments?.items?.map((item) => (
                        <div className="flex items-center justify-between border-b border-gray-300 py-5">
                          <div className="flex items-center gap-2 pr-2">
                            <PaperClipIcon className="w-5 h-5 text-gray-500 font-semibold flex-shrink-0" />
                            <div className="flex flex-col flex-grow min-w-0">
                              <p className="text-sm font-medium">
                                {item?.document_name.split(".")[0]}
                              </p>
                              <p className="text-xs font-medium mt-1 flex-shrink-0 w-max">
                                {/* ({item.document_sub_type}) */}
                                <StatusForDrawer
                                  statusName={item.document_sub_type}
                                />
                              </p>
                            </div>
                          </div>
                          <span
                            className="text-sm font-semibold text-indigo-600 cursor-pointer"
                            onClick={() => handleDownload(item?.document_id)}
                          >
                            <ArrowDownTrayIcon className="w-8 h-8 p-1 text-gray-600" />
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <div
                    className={`text-center py-2 flex ${allDocuments?.items?.length > 0
                      ? "justify-end"
                      : "justify-center"
                      } px-3.5 mt-3 `}
                ></div>
              </div>
            }

            <div className="mt-10 md:mt-6">
              <h2 className="text-sm font-semibold text-gray-900">
                Assessment Activity
              </h2>
              <ul className="mt-6 space-y-6 h-80 overflow-y-auto px-2">
                {assessmentDetails?.vqa === null && (
                  <li className="relative flex gap-x-4 mt-1">
                    {/* Line connecting items */}
                    <div className="absolute left-0 top-0 flex w-6 justify-center -bottom-6">
                      <div className="w-px bg-gray-200"></div>
                    </div>

                    {/* Circle Indicator */}
                    <div className="relative flex h-6 w-6 items-center justify-center bg-white">
                      <div className="h-2 w-2 rounded-full bg-gray-200 flex items-center justify-center ring-1 ring-gray-300">
                        <span className="text-xs font-medium text-gray-700"></span>
                      </div>
                    </div>

                    <div className="flex-auto flex">
                      <p className="py-0.5 text-xs text-gray-500 ">
                        <span className="font-medium text-gray-900">
                          {
                            assessmentDetails?.vqa_request_sponsors[0]
                              ?.sponsor_name
                          }
                        </span>
                        <span> Requested New Assessment</span>
                      </p>

                      <time className="flex-none py-0.5 text-xs text-gray-500 ">
                        Today
                      </time>
                    </div>
                  </li>
                )}

                {assessmentDetails?.vqa !== null &&
                  activity?.items
                    ?.sort(
                      (a, b) =>
                        new Date(b?.activity_done_on) -
                        new Date(a?.activity_done_on)
                    )
                    ?.map((item, index, array) => {
                      const isLatest =
                        item?.activity_done_on ===
                        array.reduce((latest, current) =>
                          new Date(current?.activity_done_on) >=
                            new Date(latest?.activity_done_on)
                            ? current
                            : latest
                        )?.activity_done_on;

                      return (
                        <li key={index} className="relative flex gap-x-4">
                          {/* Line connecting items */}
                          <div className="absolute left-0 top-0 flex w-6 justify-center -bottom-6">
                            <div className="w-px bg-gray-200"></div>
                          </div>

                          {/* Circle or Profile Picture */}
                          <div className="relative flex h-6 w-6 items-center justify-center bg-white">
                            {item?.activity_comments && !isLatest ? (
                              <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-gray-300">
                                <span className="text-xs font-medium text-gray-700">
                                  {item?.activity_done_by
                                    ?.charAt(0)
                                    .toUpperCase() || "A"}
                                </span>
                              </div>
                            ) : (
                              <div className="absolute right-0 bottom-0 text-xs text-green-500">
                                <CheckCircleIcon className="size-6 absolute right-0 bottom-0 text-green-600" />
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
              </ul>
            </div>
          </div>
        )}
      </div>

      <JoinFlowModals
        openModal={openModal}
        setOpenModal={setOpenModal}
        onConfirm={handleConfirmJoinVQA}
        servicesScopes={assessmentDetails?.capabilities || []}
        notificationOpen={notificationOpen}
        setNotificationOpen={setNotificationOpen}
        notificationTitle={notificationTitle}
        notificationDesc={notificationDesc}
        notificationError={notificationError}
      />
    </div>
  );
};

function JoinFlowModals({
  openModal,
  setOpenModal,
  onConfirm,
  servicesScopes,
  notificationOpen,
  setNotificationOpen,
  notificationTitle,
  notificationDesc,
  notificationError,
}) {
  return (
    <>
      <JoinAssessmentConfirmationModal
        open={openModal}
        setOpen={setOpenModal}
        onConfirm={onConfirm}
        servicesScopes={servicesScopes}
      />
      <NotificationModal
        open={notificationOpen}
        setOpen={setNotificationOpen}
        title={notificationTitle}
        desc={notificationDesc}
        error={notificationError}
      />
    </>
  );
}
